import { useContext, useEffect, useRef, useState } from 'react';
import { IconInfo, IconAlert } from '@belong/icons';
import {
  MoneyRange,
  Money,
  Tooltip as TooltipV2,
  ButtonBase,
  Text,
} from '@belong/ui';
import classNames from 'classnames/bind';
import clsx from 'clsx';
import GeneralIcon, { GENERAL_ICONS } from 'components/GeneralIcon/GeneralIcon';
import Label from 'components/Label/Label';
import Tooltip from 'components/Tooltip/Tooltip';
import ButtonBaseLegacy from 'corecomponents/ButtonBase/ButtonBase';
import { Box, Flex, Text as TextLegacy } from 'design-system';
import { filter, startCase } from 'es-toolkit/compat';
import {
  ConsentStatus,
  MaintenanceAssessment,
  MaintenanceBundleType,
  MaintenanceResponsibility,
} from 'models/enums';
import { PreventativeBundleProgress } from 'pages/PostInspectionFlow/steps/Improvements/Approval/Bundle/PreventativeBundleProgress/PreventativeBundleProgress';
import { VacancyReductionBundleProgress } from 'pages/PostInspectionFlow/steps/Improvements/Approval/Bundle/VacancyReductionBundleProgress/VacancyReductionBundleProgress';
import {
  BUNDLE_CONFIG,
  bundleTypeDescription,
  getPaymentBreakdown,
  getPayments,
  isTBD,
} from 'pages/PostInspectionFlow/steps/Improvements/utils';
import { groupRecommendedImprovements } from 'pages/PostInspectionFlow/utils';
import PropTypes from 'prop-types';
import { getString } from 'strings';
import { POST_INSPECTION_FLOW_STRINGS } from 'strings/post-inspection-flow';
import { pluralizeWithoutValue } from 'utils/pluralize';
import {
  ResidentResponsibilityLabel,
  SharedResponsibilityLabel,
} from '../../../../Labels/Labels';
import TwoColumnLayout from '../../Components/TwoColumnLayout/TwoColumnLayout';
import { ApprovalContext } from '../ApprovalContext';
import styles from './Bundle.module.css';
import { BundleItem } from './BundleItem/BundleItem';
import BundlePrice, { StrikedPrice } from './BundlePrice/BundlePrice';

const cx = classNames.bind(styles);

const propTypes = {
  bundle: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  onClick: PropTypes.func.isRequired,
  onToggle: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  isMonthly: PropTypes.bool,
  isGTTLDisqualifiedDueHomeownerFixingItems: PropTypes.bool,
  inspectionType: PropTypes.object,
  displayPrice: PropTypes.bool,
  improvementPresets: PropTypes.array,
};

const defaultProps = {};

const Bundle = ({
  bundle,
  onChange,
  onClick,
  onToggle,
  open,
  isMonthly,
  inspectionType,
  isGTTLDisqualifiedDueHomeownerFixingItems,
  displayPrice,
  improvementPresets,
}) => {
  const approvalContext = useContext(ApprovalContext);
  const content = useRef(null);
  const orderedBundleItems = useRef(null);
  const breakdown = getPaymentBreakdown(bundle);

  const estimatedCost = breakdown.homeowner?.estimatedCost || 0;
  const residentEstimatedCost = breakdown.resident?.estimatedCost || 0;
  const residentResponsiblity =
    estimatedCost === 0 && residentEstimatedCost !== 0;
  const sharedResponsiblity =
    estimatedCost !== 0 && residentEstimatedCost !== 0;
  const { type, displayAsPriceRange } = bundle;
  const bundleConfig = BUNDLE_CONFIG[type];
  const { maximumMonthlyPaymentPlan, homeownerPayment } = approvalContext;

  const homeownerItems = filter(
    bundle.items,
    (item) =>
      !!item.maintenancePayments.find(
        (payment) =>
          payment.paidBy === MaintenanceResponsibility.Homeowner &&
          payment.percentage !== 0,
      ),
  );

  const isRequired = homeownerItems.every(
    (homeownerItem) =>
      homeownerItem.maintenance.assessment === MaintenanceAssessment.Required,
  );

  const isPartiallyRequired = homeownerItems.find(
    (homeownerItem) =>
      homeownerItem.maintenance.assessment === MaintenanceAssessment.Required,
  );

  const isRecommended = homeownerItems.every(
    (homeownerItem) =>
      homeownerItem.maintenance.assessment ===
      MaintenanceAssessment.Recommended,
  );

  const approvedItems = filter(bundle.items, (item) => {
    const paymentBreakdown = getPayments(item.maintenancePayments);

    if (paymentBreakdown.resident?.percentage === 100) return true;
    if (!paymentBreakdown.homeowner) return false;
    if (paymentBreakdown.homeowner.percentage === 0) {
      return true;
    }

    // NOTE: NotRequired consent status means that the HO doesn't need to approve the improvement.
    return (
      paymentBreakdown.homeowner.consentStatus === ConsentStatus.Approved ||
      paymentBreakdown.homeowner.consentStatus === ConsentStatus.NotRequired
    );
  });

  let label;

  if (residentResponsiblity) {
    label = <ResidentResponsibilityLabel />;
  } else if (sharedResponsiblity) {
    label = <SharedResponsibilityLabel />;
  } else if (isRequired) {
    label = <Label text="Required" />;
  } else if (isPartiallyRequired) {
    label = <Label text="Partially Required" />;
  } else if (isRecommended) {
    label = <Label text="Recommended" />;
  }

  const bundlePercentage = (approvedItems.length / homeownerItems.length) * 100;

  const isPreventative = type === MaintenanceBundleType.Preventative;
  const isVacancyReduction = type === MaintenanceBundleType.VacancyReduction;

  const showVacancyReductionProgress =
    isVacancyReduction && bundle.items.length > 1;

  const totalEstimatedCostPrevented = bundle.items.reduce(
    (totalCost, currentItem) => {
      const currentItemCost =
        currentItem.maintenance.estimatedCostPrevented || 0;

      return totalCost + currentItemCost;
    },
    0,
  );

  const getBundleReasonTitle = () => {
    const pronoun = pluralizeWithoutValue(bundle.items.length, 'these');
    const capitalizedPronoun = startCase(pronoun);

    const subject = pluralizeWithoutValue(bundle.items.length, 'improvement');
    const capitalizedSubject = startCase(subject);

    const verbPluralizeKey = isRequired ? 'is' : 'matter';
    const verb = pluralizeWithoutValue(bundle.items.length, verbPluralizeKey);
    const capitalizedVerb = startCase(verb);

    const titleStringKey = isRequired
      ? 'approvals.importance.bundle.title.required'
      : 'approvals.importance.bundle.title.matter';

    return getString(POST_INSPECTION_FLOW_STRINGS[titleStringKey], {
      pronoun: capitalizedPronoun,
      subject: capitalizedSubject,
      verb: capitalizedVerb,
    });
  };

  const [overflowAuto, setOverflowAuto] = useState(!open);

  useEffect(() => {
    if (open) {
      setTimeout(() => {
        setOverflowAuto(false);
      }, [100]);

      return;
    }

    setOverflowAuto(true);
  }, [open]);

  useEffect(() => {
    // We only sort the items from the cheapest to most expensive on the first page load.
    // Since we don't have an itemId, we use the maintenance jobId as lookup key.
    if (!orderedBundleItems.current) {
      orderedBundleItems.current = [...bundle.items]
        .sort((itemA, itemB) => {
          // TBD items go at the very bottom
          if (itemA.isTBD) {
            return 1;
          }

          return itemA.estimatedDisplayCost > itemB.estimatedDisplayCost
            ? 1
            : -1;
        })
        .map((item) => item.maintenance.uniqueId);
    }
  }, [bundle.items]);

  // If the items were previously ordered, we'll preserve that no matter if the user chooses an option which causes
  // the improvement be in a different position. For that is that we have stored the maintenance jobId in a ref.
  const bundleItemsWithInitialOrder = orderedBundleItems.current
    ? orderedBundleItems.current.map((jobId) => {
        const bundlefilter = bundle.items.find(
          (item) => item.maintenance.uniqueId === jobId,
        );

        return bundlefilter;
      })
    : bundle.items;

  return (
    <div
      className={cx('bundle', {
        'open-borders': open && bundlePercentage < 100,
        open,
      })}
    >
      <ButtonBaseLegacy
        className={cx('bundle-header')}
        onClick={() => onToggle(type)}
      >
        <div className={cx('top-section')}>
          <div className={cx('left-section')}>
            <div className={cx('bundle-details')}>
              <Flex
                alignItems="flex-start"
                flexDirection="column"
                justifyContent={['unset', 'space-between']}
                marginRight="xs"
              >
                {!groupRecommendedImprovements && (
                  <>
                    {label && (
                      <Flex
                        justifyContent="flex-start"
                        marginBottom={['xs', '2xs']}
                      >
                        {label}
                      </Flex>
                    )}
                  </>
                )}
                <TextLegacy className={cx('initial')} fontWeight="semibold">
                  {bundleConfig.displayName} ({approvedItems.length})
                </TextLegacy>
                <p className="p1 font-semibold text-green">0% Belong Markup</p>
              </Flex>
            </div>
          </div>
          <div className={cx('right-section')}>
            {displayPrice && (
              <>
                <div className={cx('price')}>
                  {displayAsPriceRange ? (
                    <MoneyRange
                      lower={bundle.priceRange?.lowerBoundSubtotal}
                      upper={bundle.priceRange?.upperBoundSubtotal}
                      className="font-semibold"
                      format="DOLLARS_NO_CENTS"
                    />
                  ) : (
                    <>
                      {!residentResponsiblity && (
                        <BundlePrice
                          maximumMonthlyPaymentPlan={maximumMonthlyPaymentPlan}
                          homeownerPayment={homeownerPayment}
                          bundle={bundle}
                          textClassname={cx('text')}
                          isMonthly={isMonthly}
                        />
                      )}
                      {residentResponsiblity && (
                        <div className={cx('gray')}>
                          <Money
                            value={bundle.estimatedCost}
                            format="DOLLARS_NO_CENTS"
                          />
                        </div>
                      )}
                    </>
                  )}
                </div>

                <div>
                  {displayAsPriceRange && (
                    <Tooltip
                      contentStyle={{
                        width: 330,
                        backgroundColor: '#000',
                        padding: '12px',
                      }}
                      trigger={
                        <div
                          className="-ml-xs mr-xs hidden cursor-pointer md:block"
                          style={{ marginTop: '5px' }}
                        >
                          <IconInfo width={20} />
                        </div>
                      }
                    >
                      <p className="p1 text-white">
                        We&apos;ve calculated a repair range factoring in
                        potential scope changes that can affect the final cost
                        once a contractor takes a closer look. Belong will
                        negotiate the lowest final price with no mark-up.
                        Greenlighting this range empowers our team to start
                        work promptly.
                      </p>
                    </Tooltip>
                  )}
                </div>
              </>
            )}

            <div className={cx('caret-icon')}>
              <GeneralIcon icon={GENERAL_ICONS.DOWN_CARET} />
            </div>
          </div>
        </div>

        {displayPrice && (
          <div className={cx('bottom-section')}>
            {displayAsPriceRange ? (
              <MoneyRange
                lower={bundle.priceRange?.lowerBoundSubtotal}
                upper={bundle.priceRange?.upperBoundSubtotal}
                className="font-semibold"
                format="DOLLARS_NO_CENTS"
              />
            ) : (
              <>
                {!!approvedItems.length && (
                  <BundlePrice
                    bullet
                    maximumMonthlyPaymentPlan={maximumMonthlyPaymentPlan}
                    homeownerPayment={homeownerPayment}
                    bundle={bundle}
                    textClassname={cx('text')}
                  />
                )}
              </>
            )}
            {displayAsPriceRange && (
              <Tooltip
                contentStyle={{
                  width: 260,
                  backgroundColor: '#000',
                  padding: '12px',
                }}
                trigger={
                  <div
                    className="ml-xs block cursor-pointer md:hidden"
                    style={{ marginTop: '5px' }}
                  >
                    <IconInfo width={20} />
                  </div>
                }
              >
                <p className="p1 text-white">
                  We&apos;ve calculated a repair range factoring in potential
                  scope changes that can affect the final cost once a contractor
                  takes a closer look. Belong will negotiate the lowest final
                  price with no mark-up. Greenlighting this range empowers our
                  team to start work promptly.
                </p>
              </Tooltip>
            )}
          </div>
        )}

        {!groupRecommendedImprovements && (
          <>
            {isPreventative && !!totalEstimatedCostPrevented && (
              <PreventativeBundleProgress
                approvedItems={approvedItems}
                totalEstimatedCostPrevented={totalEstimatedCostPrevented}
              />
            )}
            {showVacancyReductionProgress && (
              <VacancyReductionBundleProgress
                approvedItems={approvedItems}
                bundle={bundle}
              />
            )}
          </>
        )}
      </ButtonBaseLegacy>
      <div
        ref={content}
        className={cx('content', { overflowAuto: !open || overflowAuto })}
        style={{ maxHeight: open ? content.current.scrollHeight : '0px' }}
      >
        <div className={cx('dashed-separator')} />

        <Flex className={cx('why-reason')} flexDirection="column">
          <div className="mb-2xs mt-md">
            <TextLegacy fontWeight="semibold">
              {getBundleReasonTitle()}
            </TextLegacy>
          </div>
          <TextLegacy>{bundleTypeDescription[type]}</TextLegacy>
        </Flex>
        <div
          className={cx([
            'items-container',
            bundlePercentage === 100 && 'selected-item',
          ])}
        >
          <TwoColumnLayout
            items={bundleItemsWithInitialOrder.map((item) => (
              <BundleItem
                key={item.maintenance.uniqueId}
                item={item}
                isPreventative={isPreventative}
                onChange={onChange}
                onClick={() => onClick(item)}
                inspectionType={inspectionType}
                isGTTLDisqualifiedDueHomeownerFixingItems={
                  isGTTLDisqualifiedDueHomeownerFixingItems
                }
                improvementPresets={improvementPresets}
                bottomRight={(selected) => {
                  const {
                    costOptions,
                    maintenance: { proServiceResponsibility },
                  } = item;

                  if (
                    proServiceResponsibility ===
                    MaintenanceResponsibility.Homeowner
                  ) {
                    return (
                      <div className="flex flex-row items-center justify-end">
                        <TooltipV2
                          contentProps={{ side: 'top' }}
                          arrowProps={{ className: 'fill-bg-black' }}
                          content={
                            <div
                              className="z-toaston mx-xs rounded bg-black p-xs text-white "
                              style={{ width: 300 }}
                            >
                              Warning: This option typically results in delays
                              that increase vacancy, and quality issues that
                              require additional repairs (often damaging the
                              relationship with your future Residents).
                            </div>
                          }
                        >
                          <ButtonBase
                            className="z-40 ml-[8px] flex cursor-pointer items-center fill-white text-white"
                            type="button"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <IconAlert
                              width={20}
                              height={16}
                              className={clsx('text-red')}
                            />
                          </ButtonBase>
                        </TooltipV2>
                        <Text className="ml-2xs text-[1.2rem] font-semibold leading-p1 text-white">
                          Do It Myself
                        </Text>
                      </div>
                    );
                  }

                  const range = item.displayAsPriceRange
                    ? {
                        lowerBound: item.priceRange?.lowerBoundSubtotal,
                        upperBound: item.priceRange?.upperBoundSubtotal,
                      }
                    : null;

                  return (
                    <Flex alignItems="flex-center" flexDirection="column">
                      {displayPrice && (
                        <Box>
                          <StrikedPrice
                            item={item}
                            textProps={{
                              color: 'white',
                              ...(isTBD(item) && {
                                fontWeight: 'semibold',
                              }),
                            }}
                            selected={selected}
                            isMonthly={isMonthly}
                            months={maximumMonthlyPaymentPlan}
                            range={range}
                            showFullPrice
                          />
                        </Box>
                      )}
                      {costOptions?.length > 1 && (
                        <Box>
                          <p className="text-[1.2rem] font-semibold leading-p1 text-white">
                            {costOptions.length} options available
                          </p>
                        </Box>
                      )}
                    </Flex>
                  );
                }}
              />
            ))}
            padding={20}
          />
        </div>
      </div>
    </div>
  );
};

Bundle.propTypes = propTypes;
Bundle.defaultProps = defaultProps;

export default Bundle;
